"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useState } from "react"
import { LandingEmailIcon, LandingPhoneIcon, LandingWhatsappIcon } from "ui"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { useAnchorScroll } from "../useAnchorScroll"

type Props = {
  data: any
}

// --- Custom breakpoint matching HeroOption2 pattern ---

const BP_DESKTOP = "@media (min-width: 1024px)"

// --- Burger / Close icons (simple, kept inline) ---

function BurgerIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 6h18M3 12h18M3 18h18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CloseMenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

// --- Styled Components (mobile-first) ---

const Header = styled.header<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  transform: ${({ visible }) => (visible ? "translateY(0)" : "translateY(-100%)")};
  transition: transform 0.5s ease-out;

  ${BP_DESKTOP} {
    padding: 16px 120px;
    height: 84px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 16px 240px;
  }
`

const HeaderSpacer = styled.div`
  height: 56px;

  ${BP_DESKTOP} {
    height: 84px;
  }
`

const LogosBurgerArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  order: 2;

  ${BP_DESKTOP} {
    order: unset;
  }
`

const LogosContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LogoDivider = styled.span`
  width: 1px;
  height: 28px;
  background-color: var(--comp-text);
  opacity: 0.3;
  flex-shrink: 0;

  ${BP_DESKTOP} {
    height: 36px;
  }
`

const LogoImage = styled(Image)`
  object-fit: contain;
`

const BurgerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--comp-text);
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;

  ${BP_DESKTOP} {
    display: none;
  }
`

const MobilePhoneBurgerArea = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  order: -1;

  ${BP_DESKTOP} {
    display: none;
  }
`

const MobilePhoneLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: var(--comp-text);
  text-decoration: none;
  flex-shrink: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const DesktopCtaArea = styled.div`
  display: none;

  ${BP_DESKTOP} {
    display: flex;
    align-items: center;
  }
`

const DesktopCtaButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  background-color: var(--inner-bg, #020a0d);
  color: var(--inner-text, white);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--inner-text, white);
    outline-offset: 2px;
  }
`

const DesktopNav = styled.nav`
  display: none;

  ${BP_DESKTOP} {
    display: flex;
    align-items: center;
    gap: 40px;
    flex: 1;
    justify-content: center;
  }
`

const NavLink = styled(Link)`
  font-size: 18px;
  font-weight: 500;
  color: var(--comp-text);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: border-color 0.2s ease;
  white-space: nowrap;

  &:hover {
    font-weight: 700;
    border-bottom-color: var(--comp-text);
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const MobileMenuOverlay = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--comp-bg);
  z-index: 99;
  padding: 16px 24px;
  overflow-y: auto;
  transform: ${({ open }) => (open ? "translateY(0)" : "translateY(-100%)")};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transition: transform 0.3s ease;

  ${BP_DESKTOP} {
    display: none;
  }
`

const MobileMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 32px;
`

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
`

const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: 400;
  color: var(--comp-text);
  text-decoration: none;
  padding: 20px 0;
  border-bottom: 1px solid var(--comp-text);
  border-bottom-color: color-mix(in srgb, var(--comp-text) 20%, transparent);
  letter-spacing: 0.09px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const MobileMenuBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
`

const MobileCtaButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 12px;
  background-color: var(--inner-bg, #020a0d);
  color: var(--inner-text, white);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
`

const MobileIconsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const MobileIconButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 34px;
  background: var(--comp-bg);
  border: 1px solid var(--comp-text);
  color: var(--comp-text);
  text-decoration: none;

  &:hover {
    opacity: 0.7;
  }
`

function ChevronLeftIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15 6L9 12L15 18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// --- Main Component ---

function LandingHeader2({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const analytics = useLandingAnalytics()
  const [menuOpen, setMenuOpen] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setHeaderVisible(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const handleAnchorClick = useAnchorScroll(useCallback(() => setMenuOpen(false), []))

  const logos = extractProperty(data, "headerLogos", true) as any[] | undefined
  const phoneLink = extractProperty(data, "headerPhoneLink") as any[] | undefined
  const emailLink = extractProperty(data, "headerEmailLink") as any[] | undefined
  const whatsappLink = extractProperty(data, "headerWhatsAppLink") as any[] | undefined
  const navLinks = extractProperty(data, "headerNavigationLinks") as any[] | undefined
  const ctaButton = extractProperty(data, "headerCtaButton") as any[] | undefined
  const ctaLink = ctaButton?.[0]

  const phoneLinkItem = phoneLink?.[0]
  const emailLinkItem = emailLink?.[0]
  const whatsappLinkItem = whatsappLink?.[0]
  const logoItems = Array.isArray(logos) ? logos : []
  const links = (Array.isArray(navLinks) ? navLinks : []).filter(
    (link: any) => link?.url && link?.name,
  )

  return (
    <>
      <Header visible={headerVisible}>
        {/* RTL: first child = right side visually on desktop (logos) */}
        {/* On mobile: order 2 pushes logos to left via CSS */}
        <LogosBurgerArea>
          {logoItems.length > 0 && (
            <LogosContainer>
              {logoItems.map((logo: any, i: number) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {i > 0 && <LogoDivider />}
                  <LogoImage
                    src={getUmbracoMediaUrl(logo?.url || "/")}
                    width={87}
                    height={46}
                    alt={logo?.altText || ""}
                  />
                </span>
              ))}
            </LogosContainer>
          )}
        </LogosBurgerArea>

        {/* Desktop navigation — center */}
        {links.length > 0 && (
          <DesktopNav>
            {links.map((link: any, i: number) => {
              const href = getLinkHrefFromMultiUrlPickerItem(link)
              return (
                <NavLink
                  key={i}
                  href={href}
                  target={link.target || undefined}
                  rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    analytics.menuClick(link.name)
                    handleAnchorClick(e, href)
                  }}
                >
                  {link.name}
                </NavLink>
              )
            })}
          </DesktopNav>
        )}

        {/* Mobile: phone icon + burger (hidden on desktop) */}
        {/* On mobile: order -1 pushes to right via CSS */}
        <MobilePhoneBurgerArea>
          <BurgerButton
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={
              menuOpen ? (isRtl ? "סגור תפריט" : "Close menu") : isRtl ? "פתח תפריט" : "Open menu"
            }
            aria-expanded={menuOpen}
          >
            {menuOpen ? <CloseMenuIcon /> : <BurgerIcon />}
          </BurgerButton>
          {phoneLinkItem?.url && (
            <MobilePhoneLink
              href={getLinkHrefFromMultiUrlPickerItem(phoneLinkItem as any)}
              aria-label={isRtl ? "התקשר" : "Call"}
              onClick={() => analytics.menuClick("phone")}
            >
              <LandingPhoneIcon />
            </MobilePhoneLink>
          )}
        </MobilePhoneBurgerArea>

        {/* Desktop: CTA button (hidden on mobile) */}
        {ctaLink && (
          <DesktopCtaArea>
            <DesktopCtaButton
              href={getLinkHrefFromMultiUrlPickerItem(ctaLink)}
              target={ctaLink.target || undefined}
              rel={ctaLink.target === "_blank" ? "noopener noreferrer" : undefined}
              onClick={() => analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.header)}
            >
              {ctaLink.name}
            </DesktopCtaButton>
          </DesktopCtaArea>
        )}
      </Header>
      <HeaderSpacer />

      {/* Mobile menu overlay */}
      <MobileMenuOverlay open={menuOpen}>
        <MobileMenuContent>
          <MobileNavLinks>
            {links.map((link: any, i: number) => {
              const href = getLinkHrefFromMultiUrlPickerItem(link as any)
              return (
                <MobileNavLink
                  key={i}
                  href={href}
                  target={link.target || undefined}
                  rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    analytics.menuClick(link.name)
                    handleAnchorClick(e, href)
                  }}
                >
                  {link.name}
                  <ChevronLeftIcon />
                </MobileNavLink>
              )
            })}
          </MobileNavLinks>

          <MobileMenuBottom>
            {ctaLink && (
              <MobileCtaButton
                href={getLinkHrefFromMultiUrlPickerItem(ctaLink as any)}
                onClick={() => {
                  analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.header)
                  setMenuOpen(false)
                }}
              >
                {ctaLink.name}
              </MobileCtaButton>
            )}
            <MobileIconsRow>
              {phoneLinkItem?.url && (
                <MobileIconButton
                  href={getLinkHrefFromMultiUrlPickerItem(phoneLinkItem as any)}
                  aria-label={isRtl ? "התקשר" : "Call"}
                  onClick={() => analytics.menuClick("phone")}
                >
                  <LandingPhoneIcon />
                </MobileIconButton>
              )}
              {emailLinkItem?.url && (
                <MobileIconButton
                  href={getLinkHrefFromMultiUrlPickerItem(emailLinkItem as any)}
                  aria-label={isRtl ? "שלח מייל" : "Email"}
                  onClick={() => analytics.menuClick("email")}
                >
                  <LandingEmailIcon />
                </MobileIconButton>
              )}
              {whatsappLinkItem?.url && (
                <MobileIconButton
                  href={getLinkHrefFromMultiUrlPickerItem(whatsappLinkItem as any)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  onClick={() => analytics.menuClick("whatsapp")}
                >
                  <LandingWhatsappIcon />
                </MobileIconButton>
              )}
            </MobileIconsRow>
          </MobileMenuBottom>
        </MobileMenuContent>
      </MobileMenuOverlay>
    </>
  )
}

export default LandingHeader2
